import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { GameDTO, WeekSummaryDTO } from '@pickem/shared';
import { useApi } from '../../lib/useApi';
import { Spinner, ErrorBanner, Card, Field } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { formatDateTime, weekStateLabel } from '../../lib/format';
import { QrPanel } from './QrPanel';
import { GamesPanel } from './GamesPanel';
import { SubmissionsPanel } from './SubmissionsPanel';
import { AdminWeekResults } from './AdminWeekResults';

interface WeekDetail {
  week: WeekSummaryDTO & {
    tokenCreatedAt: string | null;
    calculatedAt: string | null;
    hasToken: boolean;
  };
  games: GameDTO[];
  submissions: number;
  syncState: { lastRunAt: string | null; lastSuccessAt: string | null; lastError: string | null } | null;
}

export function AdminWeekDetailPage() {
  const { id } = useParams();
  const { data, error, loading, reload } = useApi<WeekDetail>(id ? `/admin/weeks/${id}` : null);
  const [action, setAction] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [actionErr, setActionErr] = useState('');
  const [tab, setTab] = useState<'overview' | 'submissions' | 'results'>('overview');

  async function run(label: string, fn: () => Promise<string>) {
    setAction(label);
    setActionMsg('');
    setActionErr('');
    try {
      const msg = await fn();
      setActionMsg(msg);
      reload();
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : `Failed: ${label}`);
    } finally {
      setAction('');
    }
  }

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;
  if (!data || !id) return null;

  const eligibleGames = data.games.filter((g) => g.eligible);
  const hasTiebreaker = data.games.some((g) => g.isTiebreaker);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to="/admin/weeks" className="text-sm text-muted underline">
            ← All weeks
          </Link>
          <h1 className="font-display text-4xl uppercase text-ink">{data.week.displayName}</h1>
          <div className="mt-1 flex flex-wrap gap-2 text-xs">
            <span className="chip bg-grove/10 text-grove">{weekStateLabel(data.week.state)}</span>
            <span className="chip bg-line text-muted">{data.submissions} submissions</span>
            {data.week.calcStatus === 'CALCULATED' && (
              <span className="chip bg-success/15 text-success">
                Results calculated {formatDateTime(data.week.calculatedAt)}
              </span>
            )}
          </div>
        </div>
        <Link to={`/results/${id}`} className="btn-outline">
          View results page
        </Link>
      </div>

      <div className="flex gap-2 border-b border-line">
        <button
          className={`px-3 py-2 text-sm font-medium ${
            tab === 'overview' ? 'border-b-2 border-grove text-grove' : 'text-muted'
          }`}
          onClick={() => setTab('overview')}
        >
          Overview
        </button>
        <button
          className={`px-3 py-2 text-sm font-medium ${
            tab === 'submissions' ? 'border-b-2 border-grove text-grove' : 'text-muted'
          }`}
          onClick={() => setTab('submissions')}
        >
          Submissions
        </button>
        <button
          className={`px-3 py-2 text-sm font-medium ${
            tab === 'results' ? 'border-b-2 border-grove text-grove' : 'text-muted'
          }`}
          onClick={() => setTab('results')}
        >
          Results
        </button>
      </div>

      {tab === 'overview' && (
        <>
          {/* Operational actions */}
          <Card>
            <h3 className="mb-3 font-display text-2xl uppercase text-ink">Actions</h3>
            <div className="flex flex-wrap gap-2">
              <button
                className="btn-primary"
                disabled={!!action}
                onClick={() =>
                  run('sync', async () => {
                    const r = await api.post<{ summary: { created: number; updated: number } }>(
                      `/admin/weeks/${id}/sync`,
                    );
                    return `Schedule synced: ${r.summary.created} created, ${r.summary.updated} updated.`;
                  })
                }
              >
                {action === 'sync' ? 'Syncing…' : 'Sync NFL schedule'}
              </button>
              <button
                className="btn-outline"
                disabled={!!action}
                onClick={() =>
                  run('scores', async () => {
                    const r = await api.post<{ summary: { updated: number; skippedLocked: number } }>(
                      `/admin/weeks/${id}/refresh-scores`,
                    );
                    return `Scores refreshed: ${r.summary.updated} updated (${r.summary.skippedLocked} locked games skipped).`;
                  })
                }
              >
                {action === 'scores' ? 'Refreshing…' : 'Refresh scores'}
              </button>
              <button
                className="btn-grove"
                disabled={!!action}
                onClick={() =>
                  run('calc', async () => {
                    const r = await api.post<{ result: { entriesScored: number } }>(
                      `/admin/weeks/${id}/calculate`,
                    );
                    return `Results calculated — ${r.result.entriesScored} entries scored.`;
                  })
                }
              >
                {action === 'calc'
                  ? 'Calculating…'
                  : data.week.calcStatus === 'CALCULATED'
                    ? 'Recalculate results'
                    : 'Calculate results'}
              </button>
            </div>
            {actionMsg && <p className="mt-2 text-sm text-success">{actionMsg}</p>}
            {actionErr && <p className="mt-2 text-sm text-danger">{actionErr}</p>}
            {data.syncState && (
              <p className="mt-2 text-xs text-muted">
                Last successful sync: {formatDateTime(data.syncState.lastSuccessAt)}
                {data.syncState.lastError && (
                  <span className="text-danger"> · last error: {data.syncState.lastError}</span>
                )}
              </p>
            )}
            {!hasTiebreaker && eligibleGames.length > 0 && (
              <p className="mt-2 text-sm text-amber-dark">
                ⚠ No Sunday Night tiebreaker game is designated yet. Set one in the Games section below.
              </p>
            )}
          </Card>

          <WeekSettings weekId={id} week={data.week} onSaved={reload} />

          <QrPanel weekId={id} weekNumber={data.week.weekNumber} weekName={data.week.displayName} />

          <GamesPanel weekId={id} games={data.games} onChanged={reload} />
        </>
      )}

      {tab === 'submissions' && <SubmissionsPanel weekId={id} />}
      {tab === 'results' && <AdminWeekResults weekId={id} />}
    </div>
  );
}

function WeekSettings({
  weekId,
  week,
  onSaved,
}: {
  weekId: string;
  week: WeekDetail['week'];
  onSaved: () => void;
}) {
  const [displayName, setDisplayName] = useState(week.displayName);
  const [enabled, setEnabled] = useState(week.enabled);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    setErr('');
    try {
      await api.patch(`/admin/weeks/${weekId}`, {
        displayName,
        enabled,
      });
      setMsg('Saved.');
      onSaved();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Unable to save.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h3 className="mb-3 font-display text-2xl uppercase text-ink">Week settings</h3>
      <form onSubmit={save}>
        <Field label="Display name" htmlFor="displayName">
          <input
            id="displayName"
            className="input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </Field>
        <div className="mb-4 grid grid-cols-1 gap-3 rounded border border-line bg-paper/50 p-3 sm:grid-cols-2">
          <div>
            <div className="text-xs uppercase text-muted">Submissions open</div>
            <div className="text-sm text-ink">{formatDateTime(week.opensAt)}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted">Picks lock</div>
            <div className="text-sm text-ink">{formatDateTime(week.locksAt)}</div>
          </div>
          <p className="col-span-full text-xs text-muted">
            Set automatically: opens 8:00am the Tuesday of the week and closes 5 minutes before the
            first eligible game. Change the eligible games below to adjust.
          </p>
        </div>
        <label className="mb-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-5 w-5"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Submissions enabled (players can submit within the window)
        </label>
        <div className="flex items-center gap-3">
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save settings'}
          </button>
          {msg && <span className="text-sm text-success">{msg}</span>}
          {err && <span className="text-sm text-danger">{err}</span>}
        </div>
      </form>
    </Card>
  );
}
